import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { 
  ChevronLeft,
  ChevronRight,
  Printer,
  Sparkles,
  Package,
  Shield,
  CheckCircle2,
  Building2,
  Target,
  Zap,
  Award,
  Phone,
  Mail,
  MapPin,
  Hospital,
  Truck,
  Clock,
  DollarSign,
  Users,
  BadgeCheck,
  ShoppingCart,
  ClipboardCheck,
  Boxes,
  ArrowLeft,
  Star,
  Play,
  Microscope,
  Scan,
  Smartphone,
  Globe,
  Layers,
  Settings
} from "lucide-react";
import { Button } from "@/components/ui/button";

// Brand Assets
const STERILE_MATE_LOGO = "/logos/askisaac-b-monogram.svg";
const GEORGE_IMAGE = "https://customer-assets.emergentagent.com/job_service-widgets/artifacts/n8vnfz06_Gemini_Generated_Image_ntzefxntzefxntze%20%281%29.png";

// EVS Demo Images - ACTUAL FILMOP USA PRODUCT IMAGES
const EVS_IMAGES = {
  // Filmop USA actual product images
  workstationCart: "https://www.filmopusa.com/images/product-page-alpha.gif",
  microfiberMop: "https://www.filmopusa.com/images/product-page-mop-velcro.gif",
  chargingSystem: "https://www.filmopusa.com/images/product-page-alpha-healthcare-equodose.gif",
  wringingSystem: "https://www.filmopusa.com/images/product-page-dual-bucket-spin-n-drop.gif",
  surfaceCleaning: "https://www.filmopusa.com/images/product-page-trowel-400x200.gif",
  disposables: "https://www.filmopusa.com/images/product-page-disposables.gif",
  dustingSystems: "https://www.filmopusa.com/images/product-page-hand-dusters.gif",
  handlesHolders: "https://www.filmopusa.com/images/product-page-handles-holders.gif",
  collectionSystems: "https://www.filmopusa.com/images/product-page-delta-polaris.gif",
  floorCollection: "https://www.filmopusa.com/images/product-page-baggy-jobby.gif",
  specialty: "https://www.filmopusa.com/images/product-page-specialty-holder-uni-move.gif",
  // CleanRead ATP actual product images
  atpDevice: "https://sterilemate.com/wp-content/uploads/2024/06/atp-device.webp",
  atpHandheld: "https://sterilemate.com/wp-content/uploads/2024/05/cleanread-handheld-atp-testing-device-indi-4.webp",
  cleanOpsApp: "https://sterilemate.com/wp-content/uploads/2025/12/cleanops-apps-image-01.webp",
  atpSwabs: "https://sterilemate.com/wp-content/uploads/2024/05/cleanread-surface-atp-detection-swabs-surface-300x300.webp",
  proteinSwabs: "https://sterilemate.com/wp-content/uploads/2024/05/cleanread-surface-protein-detection-swabs-surface-protine-300x300.webp",
  aquaSwabs: "https://sterilemate.com/wp-content/uploads/2024/05/cleanread-aqua-atp-detection-swabs-surface-detection-300x300.webp",
  reportingSoftware: "https://sterilemate.com/wp-content/uploads/2024/05/cleanread-reporting-software-reporting-300x300.webp",
  cleanLogo: "https://sterilemate.com/wp-content/uploads/2024/06/clean-logo.webp",
  // Stock images for general use
  hospital: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1200&h=800&fit=crop",
  hospitalCorridor: "https://images.unsplash.com/photo-1584451049700-ec9b394f3805?w=1200&h=800&fit=crop"
};

// Filmop USA Logo
const FILMOP_LOGO = "https://www.filmopusa.com/images/Filmop-logo-Top-Website-50years.jpg";

// External links for Filmop USA products
const FILMOP_LINKS = {
  home: "https://www.filmopusa.com/index.html",
  workstationCarts: "https://www.filmopusa.com/products/product-workstation-overview.html",
  microfiberMops: "https://www.filmopusa.com/products/product-microfiber-overview.html",
  chargingSystems: "https://www.filmopusa.com/products/product-charging-overview.html",
  wringingSystems: "https://www.filmopusa.com/products/product-wringing-system-overview.html",
  surfaceCleaning: "https://www.filmopusa.com/products/product-surface-overview.html",
  disposables: "https://www.filmopusa.com/products/product-disposables-overview.html",
  dustingSystems: "https://www.filmopusa.com/products/product-specialty-dusting-systems.html",
  handlesPoles: "https://www.filmopusa.com/products/product-holders-handles.html",
  collectionSystems: "https://www.filmopusa.com/products/product-collection-overview.html",
  floorCollection: "https://www.filmopusa.com/products/product-floor-collection-overview.html",
  specialty: "https://www.filmopusa.com/products/product-specialty-overview.html"
};

// External links for CleanRead ATP products
const CLEANREAD_LINKS = {
  mainPage: "https://sterilemate.com/atp/",
  handheldDevice: "https://sterilemate.com/cleanread-handheld-atp-testing-device/",
  cleanOpsApp: "https://sterilemate.com/cleanops-app/",
  surfaceSwabs: "https://sterilemate.com/product/cleanread-surface-atp-detection-swabs/",
  proteinSwabs: "https://sterilemate.com/product/cleanread-surface-protein-detection-swabs/",
  aquaSwabs: "https://sterilemate.com/product/cleanread-aqua-atp-detection-swabs/",
  reportingSoftware: "https://sterilemate.com/product/cleanread-reporting-software/"
};

export default function EVSDemoPage() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const deckRef = useRef(null);

  const handlePrint = () => {
    window.print();
  };

  const slides = [
    // Slide 1: Title
    {
      id: "title",
      content: (
        <div className="h-full flex flex-col items-center justify-center text-center bg-gradient-to-br from-emerald-900 via-teal-900 to-slate-900 text-white p-4 sm:p-8 md:p-12">
          <img src={STERILE_MATE_LOGO} alt="AskIsaac Inc" className="w-24 sm:w-36 md:w-48 h-auto mb-4 sm:mb-6 md:mb-8" />
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/20 border border-emerald-500/30 rounded-full mb-4">
            <Sparkles className="w-4 h-4 text-emerald-400" />
            <span className="text-emerald-300 text-sm font-semibold">Hospital-Grade Solutions</span>
          </div>
          <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold mb-2 sm:mb-4">Janitorial & EVS Solutions</h1>
          <p className="text-base sm:text-xl md:text-2xl text-emerald-300 mb-4 sm:mb-6 md:mb-8">Environmental Services Excellence for Healthcare</p>
          <div className="w-16 sm:w-20 md:w-24 h-1 bg-gradient-to-r from-emerald-500 to-teal-500 mb-4 sm:mb-6 md:mb-8" />
          <p className="text-sm sm:text-lg md:text-xl text-slate-300">Powered by Sterile Mate LLC</p>
          <div className="absolute bottom-2 sm:bottom-4 md:bottom-8 text-xs sm:text-sm text-slate-500">
            Exclusive Partner: Filmop USA | CleanRead ATP Systems
          </div>
        </div>
      )
    },
    // Slide 2: Who We Are
    {
      id: "who-we-are",
      content: (
        <div className="h-full flex flex-col bg-white text-slate-800 p-3 sm:p-6 md:p-12 overflow-y-auto">
          <h2 className="text-lg sm:text-2xl md:text-3xl font-bold text-emerald-700 mb-3 sm:mb-6 md:mb-8 flex items-center gap-2 sm:gap-3">
            <Building2 className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-emerald-600 flex-shrink-0" />
            Who We Are
          </h2>
          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8">
            <div className="space-y-4">
              <p className="text-sm sm:text-base md:text-lg text-slate-700 leading-relaxed">
                <strong className="text-emerald-700">Sterile Mate LLC</strong> has been the trusted partner for healthcare facilities across Texas since 2020. Born during the COVID-19 crisis, we understand the critical nature of hospital cleanliness and infection prevention.
              </p>
              <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
                <h3 className="font-bold text-emerald-800 mb-3">Our Mission</h3>
                <p className="text-sm text-slate-700">To ensure every healthcare facility has access to premium-quality janitorial and EVS supplies — on time, every time, at competitive prices.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-emerald-100 rounded-xl p-3 text-center border border-emerald-200">
                  <p className="text-2xl sm:text-3xl font-bold text-emerald-600">5+</p>
                  <p className="text-xs sm:text-sm text-slate-600">Years Experience</p>
                </div>
                <div className="bg-emerald-100 rounded-xl p-3 text-center border border-emerald-200">
                  <p className="text-2xl sm:text-3xl font-bold text-emerald-600">100+</p>
                  <p className="text-xs sm:text-sm text-slate-600">Healthcare Clients</p>
                </div>
              </div>
            </div>
            <div className="relative rounded-xl overflow-hidden shadow-lg">
              <img 
                src={EVS_IMAGES.hospital} 
                alt="Healthcare facility" 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/60 to-transparent flex items-end p-4">
                <p className="text-white font-semibold text-sm">Serving the Texas Medical Center & Beyond</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    // Slide 3: The Challenge
    {
      id: "challenge",
      content: (
        <div className="h-full flex flex-col bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white p-3 sm:p-6 md:p-12 overflow-y-auto">
          <h2 className="text-lg sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-6 md:mb-8 flex items-center gap-2 sm:gap-3">
            <Target className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-amber-400 flex-shrink-0" />
            The Challenge Healthcare Facilities Face
          </h2>
          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <div className="space-y-4">
              <div className="bg-red-900/30 border border-red-500/30 rounded-xl p-4">
                <h3 className="font-bold text-red-400 mb-2 flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Supply Chain Delays
                </h3>
                <p className="text-slate-300 text-sm">GPO backorders can take weeks. When you&apos;re out of critical cleaning supplies, patient safety is at risk.</p>
              </div>
              <div className="bg-amber-900/30 border border-amber-500/30 rounded-xl p-4">
                <h3 className="font-bold text-amber-400 mb-2 flex items-center gap-2">
                  <DollarSign className="w-5 h-5" />
                  Price Volatility
                </h3>
                <p className="text-slate-300 text-sm">Emergency sourcing often means paying premium prices. Limited vendor options = no negotiating power.</p>
              </div>
              <div className="bg-violet-900/30 border border-violet-500/30 rounded-xl p-4">
                <h3 className="font-bold text-violet-400 mb-2 flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Compliance Concerns
                </h3>
                <p className="text-slate-300 text-sm">Hospital-grade products must meet strict standards. Generic alternatives may not pass infection control requirements.</p>
              </div>
            </div>
            <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
              <h3 className="font-bold text-xl mb-4 text-center">The Real Cost</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between border-b border-slate-700 pb-3">
                  <span className="text-slate-400">Annual EVS Supply Spend</span>
                  <span className="font-bold text-emerald-400">$500K - $2M+</span>
                </div>
                <div className="flex items-center justify-between border-b border-slate-700 pb-3">
                  <span className="text-slate-400">Emergency Markup</span>
                  <span className="font-bold text-red-400">+30-50%</span>
                </div>
                <div className="flex items-center justify-between border-b border-slate-700 pb-3">
                  <span className="text-slate-400">Time Spent Sourcing</span>
                  <span className="font-bold text-amber-400">40+ hrs/month</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">Stockout Risk</span>
                  <span className="font-bold text-red-400">HIGH</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    // Slide 4: Filmop USA Partnership
    {
      id: "filmop",
      content: (
        <div className="h-full flex flex-col bg-gradient-to-br from-blue-900 via-slate-900 to-slate-900 text-white p-3 sm:p-6 md:p-12 overflow-y-auto">
          <div className="flex items-center gap-4 mb-4 sm:mb-6">
            <img src={FILMOP_LOGO} alt="Filmop USA" className="h-10 sm:h-12 md:h-16 bg-white rounded-lg p-1" />
            <div>
              <h2 className="text-lg sm:text-2xl md:text-3xl font-bold">Primary Janitorial Partner</h2>
              <p className="text-blue-300 text-sm">50 Years of Manufacturing Excellence</p>
            </div>
          </div>
          <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            <div className="space-y-4">
              <div className="bg-white/10 backdrop-blur rounded-xl p-4 border border-blue-500/30">
                <h3 className="font-bold text-blue-400 mb-3 flex items-center gap-2">
                  <Globe className="w-5 h-5" />
                  World-Class Manufacturing
                </h3>
                <ul className="text-sm text-blue-100 space-y-2">
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-blue-400" /> Precision manufactured in Italy</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-blue-400" /> Solar & renewable energy production</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-blue-400" /> 200+ global patents</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-blue-400" /> Serving 80+ countries</li>
                </ul>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-4 border border-emerald-500/30">
                <h3 className="font-bold text-emerald-400 mb-3 flex items-center gap-2">
                  <Award className="w-5 h-5" />
                  Healthcare Leadership
                </h3>
                <p className="text-emerald-100 text-sm">Leader in European healthcare facilities with equipment engineered for demanding EVS departments.</p>
              </div>
            </div>
            <a href={FILMOP_LINKS.workstationCarts} target="_blank" rel="noopener noreferrer" className="bg-white rounded-xl p-4 flex items-center justify-center hover:ring-2 hover:ring-blue-400 transition-all">
              <img src={EVS_IMAGES.workstationCart} alt="Filmop Alpha Workstation Cart" className="max-h-full max-w-full object-contain" />
            </a>
            <a href={FILMOP_LINKS.chargingSystems} target="_blank" rel="noopener noreferrer" className="bg-white rounded-xl p-4 flex items-center justify-center hover:ring-2 hover:ring-blue-400 transition-all">
              <img src={EVS_IMAGES.chargingSystem} alt="Filmop Equodose Charging System" className="max-h-full max-w-full object-contain" />
            </a>
          </div>
          <div className="mt-4 bg-blue-600/30 rounded-xl p-4 border border-blue-500/30">
            <p className="text-center text-blue-100 text-sm">
              <strong className="text-white">Why Filmop?</strong> Touch-free systems decrease pathogen spread • Ergonomic design reduces worker strain • Better cleaning decreases HAIs
            </p>
          </div>
        </div>
      )
    },
    // Slide 5: Filmop Video
    {
      id: "filmop-video",
      content: (
        <div className="h-full flex flex-col bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white p-3 sm:p-6 md:p-12 overflow-y-auto">
          <div className="flex items-center gap-4 mb-4 sm:mb-6">
            <img src={FILMOP_LOGO} alt="Filmop USA" className="h-10 sm:h-12 md:h-16 bg-white rounded-lg p-1" />
            <div>
              <h2 className="text-lg sm:text-2xl md:text-3xl font-bold flex items-center gap-2">
                <Play className="w-6 h-6 sm:w-8 sm:h-8 text-red-500" />
                See Filmop in Action
              </h2>
              <p className="text-blue-300 text-sm">Watch how Filmop revolutionizes healthcare cleaning</p>
            </div>
          </div>
          <div className="flex-1 flex items-center justify-center">
            <div className="w-full max-w-4xl aspect-video rounded-xl overflow-hidden shadow-2xl border-2 border-blue-500/30">
              <iframe
                src="https://www.youtube.com/embed/Mq3Zi6M0hMI?rel=0"
                title="Filmop USA Corporate Video"
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
          <div className="mt-4 flex justify-center gap-4">
            <a 
              href="https://www.youtube.com/watch?v=Mq3Zi6M0hMI" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-500 rounded-lg transition-colors text-sm font-semibold"
            >
              <Play className="w-4 h-4" />
              Watch on YouTube
            </a>
            <a 
              href={FILMOP_LINKS.home} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg transition-colors text-sm font-semibold"
            >
              <Globe className="w-4 h-4" />
              Visit Filmop USA
            </a>
          </div>
        </div>
      )
    },
    // Slide 6: Filmop Product Categories
    {
      id: "filmop-products",
      content: (
        <div className="h-full flex flex-col bg-white text-slate-800 p-3 sm:p-6 md:p-12 overflow-y-auto">
          <div className="flex items-center gap-3 mb-4 sm:mb-6">
            <img src={FILMOP_LOGO} alt="Filmop USA" className="h-8 sm:h-10" />
            <h2 className="text-lg sm:text-2xl md:text-3xl font-bold text-blue-700">Filmop Product Portfolio</h2>
          </div>
          <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-3">
            <a href={FILMOP_LINKS.workstationCarts} target="_blank" rel="noopener noreferrer" className="bg-slate-50 rounded-xl p-3 border border-slate-200 flex flex-col items-center hover:ring-2 hover:ring-blue-400 hover:border-blue-400 transition-all">
              <img src={EVS_IMAGES.workstationCart} alt="Workstation Carts" className="h-20 md:h-24 object-contain mb-2" />
              <p className="font-semibold text-blue-800 text-xs text-center">Workstation Carts</p>
            </a>
            <a href={FILMOP_LINKS.microfiberMops} target="_blank" rel="noopener noreferrer" className="bg-slate-50 rounded-xl p-3 border border-slate-200 flex flex-col items-center hover:ring-2 hover:ring-emerald-400 hover:border-emerald-400 transition-all">
              <img src={EVS_IMAGES.microfiberMop} alt="Microfiber Mops" className="h-20 md:h-24 object-contain mb-2" />
              <p className="font-semibold text-emerald-800 text-xs text-center">Microfiber Mops</p>
            </a>
            <a href={FILMOP_LINKS.chargingSystems} target="_blank" rel="noopener noreferrer" className="bg-slate-50 rounded-xl p-3 border border-slate-200 flex flex-col items-center hover:ring-2 hover:ring-cyan-400 hover:border-cyan-400 transition-all">
              <img src={EVS_IMAGES.chargingSystem} alt="Charging Systems" className="h-20 md:h-24 object-contain mb-2" />
              <p className="font-semibold text-cyan-800 text-xs text-center">Charging Systems</p>
            </a>
            <a href={FILMOP_LINKS.wringingSystems} target="_blank" rel="noopener noreferrer" className="bg-slate-50 rounded-xl p-3 border border-slate-200 flex flex-col items-center hover:ring-2 hover:ring-violet-400 hover:border-violet-400 transition-all">
              <img src={EVS_IMAGES.wringingSystem} alt="Wringing Systems" className="h-20 md:h-24 object-contain mb-2" />
              <p className="font-semibold text-violet-800 text-xs text-center">Wringing Systems</p>
            </a>
            <a href={FILMOP_LINKS.surfaceCleaning} target="_blank" rel="noopener noreferrer" className="bg-slate-50 rounded-xl p-3 border border-slate-200 flex flex-col items-center hover:ring-2 hover:ring-amber-400 hover:border-amber-400 transition-all">
              <img src={EVS_IMAGES.surfaceCleaning} alt="Surface Cleaning" className="h-20 md:h-24 object-contain mb-2" />
              <p className="font-semibold text-amber-800 text-xs text-center">Surface Cleaning</p>
            </a>
            <a href={FILMOP_LINKS.dustingSystems} target="_blank" rel="noopener noreferrer" className="bg-slate-50 rounded-xl p-3 border border-slate-200 flex flex-col items-center hover:ring-2 hover:ring-teal-400 hover:border-teal-400 transition-all">
              <img src={EVS_IMAGES.dustingSystems} alt="Dusting Systems" className="h-20 md:h-24 object-contain mb-2" />
              <p className="font-semibold text-teal-800 text-xs text-center">Dusting Systems</p>
            </a>
            <a href={FILMOP_LINKS.collectionSystems} target="_blank" rel="noopener noreferrer" className="bg-slate-50 rounded-xl p-3 border border-slate-200 flex flex-col items-center hover:ring-2 hover:ring-slate-400 hover:border-slate-400 transition-all">
              <img src={EVS_IMAGES.collectionSystems} alt="Collection Systems" className="h-20 md:h-24 object-contain mb-2" />
              <p className="font-semibold text-slate-800 text-xs text-center">Collection Systems</p>
            </a>
            <a href={FILMOP_LINKS.handlesPoles} target="_blank" rel="noopener noreferrer" className="bg-slate-50 rounded-xl p-3 border border-slate-200 flex flex-col items-center hover:ring-2 hover:ring-rose-400 hover:border-rose-400 transition-all">
              <img src={EVS_IMAGES.handlesHolders} alt="Handles & Poles" className="h-20 md:h-24 object-contain mb-2" />
              <p className="font-semibold text-rose-800 text-xs text-center">Handles & Poles</p>
            </a>
          </div>
        </div>
      )
    },
    // Slide 7: CleanRead ATP Systems
    {
      id: "cleanread",
      content: (
        <div className="h-full flex flex-col bg-gradient-to-br from-violet-900 via-purple-900 to-slate-900 text-white p-3 sm:p-6 md:p-12 overflow-y-auto">
          <div className="flex items-center gap-3 mb-4">
            <img src={EVS_IMAGES.cleanLogo} alt="CleanRead" className="h-8 sm:h-10" />
            <div>
              <h2 className="text-lg sm:text-2xl md:text-3xl font-bold">CleanRead™ ATP Verification</h2>
              <p className="text-violet-300 text-sm">Accurate, Fast, Reliable Cleanliness Verification</p>
            </div>
          </div>
          <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            <div className="space-y-4">
              <div className="bg-white/10 backdrop-blur rounded-xl p-4 border border-violet-500/30">
                <h3 className="font-bold text-violet-400 mb-2 flex items-center gap-2">
                  <Scan className="w-5 h-5" />
                  ATP Testing Device
                </h3>
                <p className="text-violet-100 text-xs mb-2">Instant cleanliness verification for any surface.</p>
                <ul className="text-xs text-violet-200 space-y-1">
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-violet-400" /> Results in 15 seconds</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-violet-400" /> Pass/Fail thresholds</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-violet-400" /> Data logging</li>
                </ul>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-4 border border-purple-500/30">
                <h3 className="font-bold text-purple-400 mb-2 flex items-center gap-2">
                  <Smartphone className="w-5 h-5" />
                  CleanOps App
                </h3>
                <p className="text-purple-100 text-xs mb-2">Complete cleaning ops management.</p>
                <ul className="text-xs text-purple-200 space-y-1">
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-purple-400" /> Real-time dashboard</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-purple-400" /> Task tracking</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-purple-400" /> Compliance docs</li>
                </ul>
              </div>
            </div>
            <a href={CLEANREAD_LINKS.handheldDevice} target="_blank" rel="noopener noreferrer" className="bg-white rounded-xl p-4 flex items-center justify-center hover:ring-2 hover:ring-violet-400 transition-all">
              <img src={EVS_IMAGES.atpHandheld} alt="CleanRead ATP Handheld Device" className="max-h-full max-w-full object-contain" />
            </a>
            <a href={CLEANREAD_LINKS.cleanOpsApp} target="_blank" rel="noopener noreferrer" className="bg-white rounded-xl p-4 flex items-center justify-center hover:ring-2 hover:ring-violet-400 transition-all">
              <img src={EVS_IMAGES.cleanOpsApp} alt="CleanOps Mobile App" className="max-h-full max-w-full object-contain" />
            </a>
          </div>
        </div>
      )
    },
    // Slide 8: CleanRead Applications
    {
      id: "cleanread-apps",
      content: (
        <div className="h-full flex flex-col bg-white text-slate-800 p-3 sm:p-6 md:p-12 overflow-y-auto">
          <h2 className="text-lg sm:text-2xl md:text-3xl font-bold text-violet-700 mb-3 sm:mb-6 flex items-center gap-2 sm:gap-3">
            <Microscope className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-violet-600 flex-shrink-0" />
            CleanRead Applications
          </h2>
          <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-xl p-5 border border-violet-200">
              <div className="w-12 h-12 rounded-xl bg-violet-600 flex items-center justify-center mb-4">
                <Hospital className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-bold text-violet-800 mb-2">Healthcare Facilities</h3>
              <p className="text-sm text-slate-600 mb-3">Ensure patient rooms, ORs, and high-touch surfaces meet infection control standards.</p>
              <ul className="text-xs text-slate-500 space-y-1">
                <li>• Patient room verification</li>
                <li>• OR terminal cleaning</li>
                <li>• Equipment sanitization</li>
                <li>• Compliance documentation</li>
              </ul>
            </div>
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-5 border border-emerald-200">
              <div className="w-12 h-12 rounded-xl bg-emerald-600 flex items-center justify-center mb-4">
                <Boxes className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-bold text-emerald-800 mb-2">Food Service</h3>
              <p className="text-sm text-slate-600 mb-3">Validate food prep surfaces and equipment for safety compliance.</p>
              <ul className="text-xs text-slate-500 space-y-1">
                <li>• Kitchen surface testing</li>
                <li>• Food prep validation</li>
                <li>• Equipment cleanliness</li>
                <li>• HACCP compliance</li>
              </ul>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-5 border border-blue-200">
              <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center mb-4">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-bold text-blue-800 mb-2">Commercial Facilities</h3>
              <p className="text-sm text-slate-600 mb-3">Verify cleanliness in offices, hotels, and public spaces.</p>
              <ul className="text-xs text-slate-500 space-y-1">
                <li>• Restroom verification</li>
                <li>• High-touch surfaces</li>
                <li>• Guest room inspections</li>
                <li>• Quality assurance</li>
              </ul>
            </div>
          </div>
          <div className="mt-4 bg-violet-600 rounded-xl p-4 text-white text-center">
            <p className="font-semibold">CleanRead: From verification to documentation — complete cleanliness validation in one system.</p>
          </div>
        </div>
      )
    },
    // Slide 9: Our Solution
    {
      id: "solution",
      content: (
        <div className="h-full flex flex-col bg-gradient-to-br from-emerald-900 via-teal-900 to-slate-900 text-white p-3 sm:p-6 md:p-12 overflow-y-auto">
          <h2 className="text-lg sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-6 md:mb-8 flex items-center gap-2 sm:gap-3">
            <Zap className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-emerald-400 flex-shrink-0" />
            The AskIsaac Solution
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-emerald-200 mb-6">
            Your on-demand EVS supply partner with competitive pricing, fast delivery, and hospital-grade quality assurance.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 flex-1">
            <div className="bg-white/10 backdrop-blur rounded-xl p-4 sm:p-6 border border-emerald-500/30">
              <div className="w-12 h-12 rounded-xl bg-emerald-500 flex items-center justify-center mb-4">
                <ShoppingCart className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-base sm:text-lg font-bold text-emerald-400 mb-2">One-Stop Shop</h3>
              <p className="text-emerald-100 text-xs sm:text-sm mb-3">Complete EVS catalog — Filmop equipment, CleanRead ATP, disinfectants, and more.</p>
              <ul className="space-y-1 text-xs text-emerald-200">
                <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-emerald-400" /> Filmop Cleaning Systems</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-emerald-400" /> CleanRead ATP Devices</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-emerald-400" /> Disinfectants & Sanitizers</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-emerald-400" /> Paper & Waste Products</li>
              </ul>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl p-4 sm:p-6 border border-teal-500/30">
              <div className="w-12 h-12 rounded-xl bg-teal-500 flex items-center justify-center mb-4">
                <Truck className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-base sm:text-lg font-bold text-teal-400 mb-2">Fast Fulfillment</h3>
              <p className="text-teal-100 text-xs sm:text-sm mb-3">Backorder resolution in hours, not weeks. Our supplier network ensures availability.</p>
              <ul className="space-y-1 text-xs text-teal-200">
                <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-teal-400" /> Same-Day STAT Available</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-teal-400" /> 50+ Verified Suppliers</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-teal-400" /> Nationwide Coverage</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-teal-400" /> Real-Time Tracking</li>
              </ul>
            </div>
            <div className="bg-white/10 backdrop-blur rounded-xl p-4 sm:p-6 border border-cyan-500/30">
              <div className="w-12 h-12 rounded-xl bg-cyan-500 flex items-center justify-center mb-4">
                <BadgeCheck className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-base sm:text-lg font-bold text-cyan-400 mb-2">Quality Assured</h3>
              <p className="text-cyan-100 text-xs sm:text-sm mb-3">Hospital-grade products that meet infection control standards. No compromises.</p>
              <ul className="space-y-1 text-xs text-cyan-200">
                <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-cyan-400" /> EPA Registered Products</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-cyan-400" /> Infection Control Approved</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-cyan-400" /> Brand Name & Equivalents</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-3 h-3 text-cyan-400" /> Full Documentation</li>
              </ul>
            </div>
          </div>
        </div>
      )
    },
    // Slide 10: Why Choose Us
    {
      id: "why-us",
      content: (
        <div className="h-full flex flex-col bg-gradient-to-br from-emerald-800 via-emerald-900 to-slate-900 text-white p-3 sm:p-6 md:p-12 overflow-y-auto">
          <h2 className="text-lg sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-6 md:mb-8 flex items-center gap-2 sm:gap-3">
            <Star className="w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 text-amber-400 flex-shrink-0" />
            Why Healthcare Facilities Choose Us
          </h2>
          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <div className="space-y-4">
              <div className="bg-white/10 backdrop-blur rounded-xl p-4 border border-emerald-500/30">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-emerald-400 mb-1">Hours, Not Weeks</h3>
                    <p className="text-emerald-100 text-sm">While GPO backorders languish, we find alternatives fast. STAT delivery available for emergencies.</p>
                  </div>
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-4 border border-teal-500/30">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-teal-500 flex items-center justify-center flex-shrink-0">
                    <DollarSign className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-teal-400 mb-1">Competitive Pricing</h3>
                    <p className="text-teal-100 text-sm">Our blind-bid system ensures suppliers compete for your business. No more emergency markups.</p>
                  </div>
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-4 border border-cyan-500/30">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-cyan-500 flex items-center justify-center flex-shrink-0">
                    <Shield className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-cyan-400 mb-1">Premium Partners</h3>
                    <p className="text-cyan-100 text-sm">Exclusive Filmop USA & CleanRead partnerships. World-class equipment for world-class facilities.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white/5 rounded-xl p-6 border border-white/10">
              <h3 className="font-bold text-xl mb-4 text-center text-emerald-300">Results That Matter</h3>
              <div className="space-y-4">
                <div className="text-center p-4 bg-emerald-500/20 rounded-lg">
                  <p className="text-4xl font-bold text-emerald-400">$1.2M+</p>
                  <p className="text-emerald-200 text-sm">In EVS supplies delivered</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-center p-3 bg-teal-500/20 rounded-lg">
                    <p className="text-2xl font-bold text-teal-400">98%</p>
                    <p className="text-teal-200 text-xs">On-time delivery</p>
                  </div>
                  <div className="text-center p-3 bg-cyan-500/20 rounded-lg">
                    <p className="text-2xl font-bold text-cyan-400">24hr</p>
                    <p className="text-cyan-200 text-xs">Avg response time</p>
                  </div>
                </div>
                <div className="text-center p-3 bg-amber-500/20 rounded-lg">
                  <p className="text-2xl font-bold text-amber-400">15-20%</p>
                  <p className="text-amber-200 text-xs">Average customer savings</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    // Slide 11: Contact
    {
      id: "contact",
      content: (
        <div className="h-full flex flex-col bg-gradient-to-br from-emerald-900 via-teal-900 to-slate-900 text-white p-3 sm:p-6 md:p-8">
          <h2 className="text-lg sm:text-xl md:text-2xl font-bold mb-2 sm:mb-4 text-center">Ready to Streamline Your EVS Supply Chain?</h2>
          <div className="flex-1 flex items-center justify-center min-h-0">
            <div className="text-center max-w-lg">
              <div className="bg-white/10 backdrop-blur rounded-2xl p-6 border border-emerald-500/30 mb-6">
                <img src={GEORGE_IMAGE} alt="George Broughton III" className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-xl object-cover mx-auto mb-3 border-2 border-emerald-400/30" />
                <h3 className="text-lg sm:text-xl md:text-2xl font-bold mb-0.5 sm:mb-1">George Broughton III</h3>
                <p className="text-emerald-300 text-sm sm:text-base md:text-lg mb-4">Founder, Sterile Mate LLC</p>
                <div className="space-y-2 text-xs sm:text-sm md:text-base">
                  <a href="mailto:george.broughton@sterilemate.com" className="flex items-center justify-center gap-2 hover:text-emerald-300 transition-colors">
                    <Mail className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                    <span>george.broughton@sterilemate.com</span>
                  </a>
                  <a href="tel:+18009883892" className="flex items-center justify-center gap-2 hover:text-emerald-300 transition-colors">
                    <Phone className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                    800.988.3892
                  </a>
                  <div className="flex items-center justify-center gap-2 text-slate-400 text-xs sm:text-sm">
                    <MapPin className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                    <span>Houston, TX 77067</span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Link to="/hospital/backorders" className="bg-emerald-600 hover:bg-emerald-500 transition-colors rounded-xl p-4 text-center">
                  <ShoppingCart className="w-6 h-6 mx-auto mb-2" />
                  <p className="font-semibold text-sm">Submit a Request</p>
                </Link>
                <Link to="/how-it-works" className="bg-teal-600 hover:bg-teal-500 transition-colors rounded-xl p-4 text-center">
                  <Play className="w-6 h-6 mx-auto mb-2" />
                  <p className="font-semibold text-sm">See How It Works</p>
                </Link>
              </div>
              <div className="mt-4 flex items-center justify-center gap-4">
                <img src={STERILE_MATE_LOGO} alt="AskIsaac" className="w-20 sm:w-24 h-auto opacity-50" />
                <img src={FILMOP_LOGO} alt="Filmop USA" className="h-6 sm:h-8 opacity-50 bg-white/20 rounded p-1" />
              </div>
            </div>
          </div>
          <div className="text-center text-slate-500 text-[10px] sm:text-xs pt-1 sm:pt-2">
            Exclusive Partners: Filmop USA | CleanRead ATP Systems
          </div>
        </div>
      )
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Controls - Hidden when printing */}
      <div className="print:hidden sticky top-0 z-50 bg-slate-800 border-b border-slate-700 px-3 sm:px-6 py-2 sm:py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Link to="/" className="text-slate-400 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <Button
              onClick={prevSlide}
              variant="outline"
              size="sm"
              className="border-slate-600 text-slate-300 hover:bg-slate-700 px-2 sm:px-3"
            >
              <ChevronLeft className="w-4 h-4" />
              <span className="hidden sm:inline ml-1">Previous</span>
            </Button>
            <span className="text-slate-400 text-xs sm:text-sm whitespace-nowrap">
              <span className="sm:hidden">{currentSlide + 1}/{slides.length}</span>
              <span className="hidden sm:inline">Slide {currentSlide + 1} of {slides.length}</span>
            </span>
            <Button
              onClick={nextSlide}
              variant="outline"
              size="sm"
              className="border-slate-600 text-slate-300 hover:bg-slate-700 px-2 sm:px-3"
            >
              <span className="hidden sm:inline mr-1">Next</span>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          <Button
            onClick={handlePrint}
            size="sm"
            className="bg-gradient-to-r from-emerald-600 to-teal-500 hover:from-emerald-700 hover:to-teal-600 px-2 sm:px-4"
          >
            <Printer className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Print / Save as PDF</span>
          </Button>
        </div>
      </div>

      {/* Slide Thumbnails - Hidden on mobile and when printing */}
      <div className="print:hidden hidden sm:block bg-slate-800/50 border-b border-slate-700 px-6 py-3 overflow-x-auto">
        <div className="max-w-7xl mx-auto flex gap-2">
          {slides.map((slide, idx) => (
            <button
              key={slide.id}
              onClick={() => setCurrentSlide(idx)}
              className={`flex-shrink-0 w-16 h-10 sm:w-20 sm:h-12 rounded border-2 overflow-hidden transition-all ${
                currentSlide === idx 
                  ? 'border-emerald-500 ring-2 ring-emerald-500/50' 
                  : 'border-slate-600 hover:border-slate-500'
              }`}
            >
              <div className="w-full h-full bg-slate-700 flex items-center justify-center text-xs text-slate-400">
                {idx + 1}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Mobile Slide Dots - Visible only on mobile */}
      <div className="print:hidden sm:hidden bg-slate-800/50 border-b border-slate-700 px-3 py-2 overflow-x-auto">
        <div className="flex justify-center gap-1.5">
          {slides.map((slide, idx) => (
            <button
              key={slide.id}
              onClick={() => setCurrentSlide(idx)}
              className={`flex-shrink-0 w-2.5 h-2.5 rounded-full transition-all ${
                currentSlide === idx 
                  ? 'bg-emerald-500 scale-125' 
                  : 'bg-slate-600 hover:bg-slate-500'
              }`}
              aria-label={`Go to slide ${idx + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Current Slide View - Screen */}
      <div className="print:hidden max-w-6xl mx-auto p-2 sm:p-4 md:p-8">
        <div 
          ref={deckRef}
          className="aspect-[16/9] rounded-lg sm:rounded-2xl overflow-hidden shadow-2xl border border-slate-700"
        >
          {slides[currentSlide].content}
        </div>
      </div>

      {/* Print View - All Slides */}
      <div className="hidden print:block">
        {slides.map((slide, idx) => (
          <div 
            key={slide.id} 
            className="w-full h-screen page-break-after"
            style={{ pageBreakAfter: 'always' }}
          >
            {slide.content}
          </div>
        ))}
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            size: landscape;
            margin: 0;
          }
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .page-break-after {
            page-break-after: always;
          }
        }
      `}</style>
    </div>
  );
}
